// AGC: Main admin analytics dashboard page — 4 stacked sections with a shared date range selector.
import { useState } from 'react'
import { BarChart3 } from 'lucide-react'
import { useTranslation } from '../../../hooks/useTranslation'
import { DateRangeSelector } from './DateRangeSelector'
import { OverviewSection } from './OverviewSection'
import { PopularitySection } from './PopularitySection'
import { FrictionSection } from './FrictionSection'
import { EngagementSection } from './EngagementSection'
import type { DateRange } from '../../../services/analyticsQueryService'

export function AnalyticsDashboard(): JSX.Element {
  // AGC: Shared date range — all 4 sections re-fetch when this changes
  const [range, setRange] = useState<DateRange>('7d')
  const { t } = useTranslation()

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* AGC: Page header — title + date range selector aligned on the same row */}
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('admin.analytics.title')}</h1>
        </div>
        <DateRangeSelector value={range} onChange={setRange} />
      </header>

      {/* AGC: Section 1 — high-level KPIs: total events, active users, top feature, error rate */}
      <section aria-labelledby="overview-heading">
        <h2
          id="overview-heading"
          className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3"
        >
          {t('admin.analytics.section.overview')}
        </h2>
        <OverviewSection range={range} />
      </section>

      {/* AGC: Section 2 — top features, entity creations, creation sources */}
      <section aria-labelledby="popularity-heading">
        <h2
          id="popularity-heading"
          className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3"
        >
          {t('admin.analytics.section.popularity')}
        </h2>
        <PopularitySection range={range} />
      </section>

      {/* AGC: Section 3 — voice retries, voice errors, wizard abandons, form failures */}
      <section aria-labelledby="friction-heading">
        <h2
          id="friction-heading"
          className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3"
        >
          {t('admin.analytics.section.friction')}
        </h2>
        <FrictionSection range={range} />
      </section>

      {/* AGC: Section 4 — daily active users line chart */}
      <section aria-labelledby="engagement-heading">
        <h2
          id="engagement-heading"
          className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-3"
        >
          {t('admin.analytics.section.engagement')}
        </h2>
        <EngagementSection range={range} />
      </section>
    </div>
  )
}
